import { camelize } from 'humps'
import flatten from 'lodash/flatten'
import moment from 'moment'

import filterGroupsAPI from 'api/ApiActions/Groups/FilterByGroup'
import { useAppSelector, useAppDispatch } from 'redux/Hook'
import type { Group as GroupType } from 'containers/Participants/Redux/Reducer/InitialStateType'
import { HubAdmin } from 'lib/authorization/Roles'
import { FB_FILTER_LIST as filters } from 'lib/participants/Constants'
import { setSegmentId, setFilters } from 'redux/slices/FilterBuilder'
import type { Project as ProjectType } from 'types/Project'
import {
  getFormattedDateQuestionValues,
  mergeInitialStateWithFilters,
  sortFiltersWithInitialState,
} from 'components/Participants/Group/GroupDropdown/Helper'
import {
  updateSelectedQuestions,
  updateQuestions,
} from 'redux/slices/SignUpQuestions'
import { getDateQuestionSelectionFormat } from 'components/Participants/Filters/SignUpQuestions/Helper'

type FilterType = {
  condition: string
  value: string
  fieldName: string
}

type HubType = {
  id: string
  name: string
}

type SelectedSignupFormQuestion = {
  condition: string
  fieldName: 'signup_form'
  id: string
  optionIds: Array<number>
  questionId: number
  value: string
  type: string
}

const getJoinedAndLastSequence = (condition: string) => {
  switch (condition) {
    case 'before':
      return 1
    case 'after':
      return 2
    case 'more_than':
      return 3
    case 'less_than':
      return 4

    default:
      return 1
  }
}

const getProjectSequence = (condition: string) => {
  switch (condition) {
    case 'is_aware':
      return 1
    case 'is_informed':
      return 2
    case 'is_engaged':
      return 3

    default:
      return 1
  }
}

const getFilterWithUIComponent = (
  filtersWithUIComponent: any,
  uniqueFieldName: any,
  filter: FilterType,
  roleName: string,
  hubs: HubType[],
  projects: ProjectType[],
) => {
  const { value, fieldName } = filter
  const found = getFilter(filter)

  switch (fieldName) {
    case 'status':
      {
        // Filter by import
        if (value === 'imported') {
          const data = {
            ...found,
            value: filter.value,
            condition: found?.condition,
            name: 'participantImport',
            defaultOpen: false,
            icon: 'move_to_inbox_outlined',
            component: 'ImportFilter',
          }

          filtersWithUIComponent.push(data)
        } else {
          // Filter by status
          const data = {
            ...found,
            value: filter.value,
            condition: found?.condition,
            name: 'participantStatus',
            defaultOpen: false,
            icon: 'how_to_reg_outlined',
            component: 'StatusFilter',
          }

          filtersWithUIComponent.push(data)
        }
      }

      break

    case 'suburb':
    case 'location':
    case 'participant_tag':
    case 'project_tag':
      if (found) {
        const data = {
          ...found,
          value: filter.value,
          condition: filter.condition,
          defaultOpen: false,
        }

        filtersWithUIComponent.push(data)
      }

      break

    case 'email':
      {
        const type =
          filter.condition === 'contains_set_of'
            ? 'emailContains'
            : 'emailDoesNotContains'

        if (found) {
          const data = {
            ...found,
            value: filter.value,
            condition: filter.condition,
            defaultOpen: false,
            sequence: filter.condition === 'contains_set_of' ? 1 : 2,
            type,
          }

          // This will ensure that all the textbox related to email filter appears together
          uniqueFieldName.add('email')
          filtersWithUIComponent.push({ ...data })
        }
      }

      break

    case 'login':
      {
        const type =
          filter.condition === 'contains_set_of'
            ? 'screenNameContains'
            : 'screenNameDoesNotContains'

        if (found) {
          const data = {
            ...found,
            value: filter.value,
            condition: filter.condition,
            defaultOpen: false,
            sequence: filter.condition === 'contains_set_of' ? 1 : 2,
            type,
          }

          // This will ensure that all the textbox related to screen name filter appears together
          uniqueFieldName.add('login')
          filtersWithUIComponent.push(data)
        }
      }

      break

    case 'last_visited': {
      const data = {
        fieldName: 'last_visited',
        component: 'FilterByLastSeen',
        value: filter.value,
        UIValue: filter.value,
        condition: filter.condition,
        sequence: getJoinedAndLastSequence(filter.condition),
        defaultOpen: false,
        type: camelize(`lastSeen_${filter.condition}`),
      }

      uniqueFieldName.add('last_visited')
      filtersWithUIComponent.push(data)

      break
    }
    case 'joined': {
      const data = {
        component: 'FilterByJoinDate',
        value: filter.value,
        UIValue: filter.value,
        condition: filter.condition,
        sequence: getJoinedAndLastSequence(filter.condition),
        defaultOpen: false,
        type: camelize(`join_date_${filter.condition}`),
        fieldName: 'joined',
      }

      uniqueFieldName.add('joined')
      filtersWithUIComponent.push(data)

      break
    }

    // Filter by project
    case 'project_activity':
      if (roleName === HubAdmin) {
        // Hub admins should able to view only allowed projects
        const ids = getAllowedIdForCurrentUser(projects, filter.value)
        const data = {
          ...found,
          value: ids.join(','),
          condition: filter.condition,
          sequence: getProjectSequence(filter.condition),
          defaultOpen: false,
        }

        uniqueFieldName.add('project_activity')
        filtersWithUIComponent.push(data)

        break
      }

      if (found) {
        const data = {
          ...found,
          value: filter.value,
          condition: filter.condition,
          sequence: getProjectSequence(filter.condition),
          defaultOpen: false,
        }
        uniqueFieldName.add('project_activity')
        filtersWithUIComponent.push(data)
      }
      break

    case 'hub':
      if (found) {
        if (roleName === HubAdmin) {
          // Hub admins should able to view only allowed hub
          const ids = getAllowedIdForCurrentUser(hubs, filter.value)
          const data = {
            ...found,
            value: ids.join(','),
            condition: found.condition,
            defaultOpen: false,
          }

          filtersWithUIComponent.push(data)

          break
        }

        const data = {
          ...found,
          value: filter.value,
          condition: found.condition,
          defaultOpen: false,
        }

        filtersWithUIComponent.push(data)
      }

      break
    default:
      break
  }

  return
}

const getFilter = (filter: FilterType) => {
  return filters.find((f) => f.fieldName === filter.fieldName)
}

export const getAllowedIdForCurrentUser = (
  datas: HubType[] | ProjectType[],
  value: string,
) => {
  const ids = value?.split(',')

  let filteredId = ids.map((id: string) => {
    const data = datas.find((data) => data.id == id)
    if (data) {
      return data.id
    }
  })

  filteredId = filteredId.filter((element) => element !== undefined)

  return filteredId
}

const handleViewSignupFormQuestions = (
  selectedSignupFormQuestions: Array<SelectedSignupFormQuestion>,
  questions: any[],
  dispatch: any,
) => {
  if (selectedSignupFormQuestions?.length > 0) {
    const selectedQuestions = selectedSignupFormQuestions
      .map((signupFormQuestion: SelectedSignupFormQuestion) => {
        if (signupFormQuestion.value?.length) {
          // get the matched question
          const matchedQuestion = questions.find(
            (question: { id: string }) =>
              parseInt(question.id) === signupFormQuestion.questionId,
          )

          if (matchedQuestion?.type === 'DateQuestion') {
            const formattedValue = {
              name: '',
              [`${signupFormQuestion.condition}`]: moment(
                signupFormQuestion.value,
                'DD.MM.YYYY',
              ),
            }

            return getDateQuestionSelectionFormat({
              question: matchedQuestion.question,
              value: [formattedValue],
              id: matchedQuestion.id,
              type: matchedQuestion.type,
              yearOnlyDate: matchedQuestion.yearOnlyDate,
              selected: signupFormQuestion.condition,
            })
          }
          // get the matched options
          const selectedOptionIds = signupFormQuestion.optionIds

          const matchedOptions = matchedQuestion?.questionOptions.filter(
            (option: { id: any }) => selectedOptionIds.includes(option.id),
          )
          // prep and return the payload
          return {
            id: signupFormQuestion.questionId.toString(),
            question: matchedQuestion?.question,
            value: matchedOptions,
          }
        }
      })
      .filter(Boolean) as []

    const flattenSelectedQuestions: any = flatten(selectedQuestions)
    dispatch(updateSelectedQuestions(flattenSelectedQuestions))

    // View Group: showing the answers to the selected questions.

    const updateSelectedViewQuestions = questions.map(
      (question: SignUpFilterQuestion) => {
        const matchedQuestion = flattenSelectedQuestions.find(
          (selectedQuestion: { id: string; value: any }) => {
            if (question.id === selectedQuestion.id) {
              return selectedQuestion.value
            }
          },
        )

        if (matchedQuestion?.type === 'DateQuestion') {
          // using filter because the Single question will have two different values i,.e before and after
          const matchedDateQuestions = flattenSelectedQuestions.filter(
            (selectedQuestion: { id: string; value: any }) => {
              if (question.id === selectedQuestion.id) {
                return selectedQuestion.value
              }
            },
          )

          const formattedDateValue =
            getFormattedDateQuestionValues(matchedDateQuestions)

          return {
            ...question,
            value: [formattedDateValue],
            type: question.type,
            yearOnlyDate: question.yearOnlyDate,
          }
        }

        return {
          ...question,
          value: matchedQuestion?.value,
        }
      },
    )

    dispatch(updateQuestions(updateSelectedViewQuestions))
  }
}

export const useGroupFiltersDispatch = () => {
  const dispatch = useAppDispatch()

  const selectedFilters: any = useAppSelector(
    (state) => state.filterBuilder.selectedFilters,
  )

  const hubs = useAppSelector((state) => state.hubs.data)
  const projects = useAppSelector((state) => state.projects.data)

  const { roleName } = useAppSelector((state) => state.user.user)

  const questions = useAppSelector((state) => state.signUpQuestions.questions)

  // Fetch all the filters associated with a group
  const getGroupRelatedFilters = async (group: GroupType) => {
    // Set is used to add the initial state for particular filters like email
    const uniqueFieldName = new Set()

    const response = await filterGroupsAPI.getGroup({ segmentId: group.id })
    const filtersfromAPI = response.data?.included

    dispatch(setSegmentId(response?.data?.data?.id))

    const filtersWithUIComponent: FilterType[] = []

    const selectedSignupFormQuestions: Array<SelectedSignupFormQuestion> = []

    if (filtersfromAPI?.length > 0) {
      filtersfromAPI.forEach((filter: SelectedSignupFormQuestion) => {
        if (filter.fieldName === 'signup_form') {
          selectedSignupFormQuestions.push(filter)
        }

        getFilterWithUIComponent(
          filtersWithUIComponent,
          uniqueFieldName,
          filter,
          roleName,
          hubs,
          projects,
        )
      })
    }

    // Merge initial states of filters whose input box needs to together on the UI
    const withIntialStates = mergeInitialStateWithFilters(
      uniqueFieldName,
      filtersWithUIComponent,
    )

    // Sort the filters with their fieldname so that same fieldName appears together
    const sortedFilters = sortFiltersWithInitialState(withIntialStates)
    dispatch(setFilters(sortedFilters))

    handleViewSignupFormQuestions(
      selectedSignupFormQuestions,
      questions,
      dispatch,
    )
  }

  return [getGroupRelatedFilters, {}, selectedFilters]
}
